import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Crosshair, Target, Zap, Brain, MapPin, ChevronDown } from "lucide-react";
import { Reveal, SectionTag, MaskedLine } from "@/components/Reveal";

const THEO_IMG = "https://customer-assets-lqy194kg.emergentagent.net/job_hoops-iq-academy/artifacts/4msa7t11_unnamed%20%282%29.jpg";

const PILLARS = [
  { icon: Crosshair, title: "Ball Handling", text: "Two-ball pressure series, change-of-pace combos, and live defensive reads until the ball is on a string." },
  { icon: Target, title: "Shooting Purity", text: "Mechanics rebuilt from the base up, then volume — hundreds of tracked makes with percentage goals every week." },
  { icon: Zap, title: "Agility & Explosion", text: "Deceleration, first-step burst, and lateral quickness trained like a science — not random cone drills." },
  { icon: Brain, title: "Basketball IQ", text: "Film sessions, coverage recognition, and live-read reps so the game slows down for your athlete." },
];

const FAQS = [
  { q: "Where do sessions happen?", a: "Sessions run at Nothing But Net Gym — 590 Whitney St, San Leandro, CA 94577. A dedicated facility: no crowded rec centers and no waiting for a rim." },
  { q: "What ages and levels does Theo train?", a: "Middle school through Division 1 college players, both boys and girls. Every session is scaled to the athlete — beginners build the base, advanced players get pushed." },
  { q: "How do payments work?", a: "No online payment. Pay in person at the gym — cash, Zelle, or Venmo. Private 1-on-1 sessions are $60 and group drop-ins are $30." },
  { q: "How do the college coach connections work?", a: "Theo maintains relationships with college coaches and scouts. When an athlete is genuinely ready, he makes referrals, shares film, and gives honest recruiting guidance to families. That's why filming your games matters so much — college coaches recruit off game film, so record every game you can. Theo breaks it down with you, finds your best moments, and turns them into a reel worth sending." },
  { q: "What are the training plans in the member vault?", a: "The exact workout blueprints used in private sessions — shooting programs, ball-handling progressions, agility programs. Create a free member account to unlock the full library." },
];

export default function About() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <main className="pt-16">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <div className="inline-flex items-center gap-2 border border-court-amber/40 bg-court-amber/10 rounded-full px-4 py-1.5 mb-8">
            <MapPin size={13} className="text-court-amber" />
            <span className="font-mono text-xs uppercase tracking-widest text-court-amber font-semibold">Alameda, CA • Boys & Girls • Ages 11 to D1</span>
          </div>
          <h1 data-testid="about-heading" className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.1}>Meet</MaskedLine>
            <MaskedLine delay={0.25}><span className="text-court-amber">Coach Theo.</span></MaskedLine>
          </h1>
          <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed">
            Theo runs the Basketball Lab out of private gyms in Alameda, California — training athletes from middle school all the way to Division 1 college basketball, boys and girls alike. His sessions are built on one belief: talent is everywhere, but real skill is manufactured rep by rep.
          </p>
          <p className="mt-4 text-sm sm:text-base text-zinc-400 leading-relaxed">
            Parents choose Theo because the work is personal. Every athlete gets a plan, every parent gets honest progress reports, and every serious player gets access to something money usually can't buy — a direct line to college coaches and the exact training blueprints used inside the Lab.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-6">
            {[["11–22", "Ages trained"], ["2", "Session formats"], ["D1", "Coach network"]].map(([big, small]) => (
              <div key={small}>
                <div className="font-display font-black text-3xl text-court-amber">{big}</div>
                <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mt-1">{small}</div>
              </div>
            ))}
          </div>
          <Link data-testid="about-book-cta" to="/schedule" className="btn-amber mt-9 inline-flex items-center gap-2 bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg px-8 py-3.5 rounded-lg">
            Train With Theo <ArrowRight size={18} />
          </Link>
        </div>
        <Reveal delay={0.15}>
          <div className="spotlight-frame-reverse relative overflow-hidden border border-white/10">
            <div className="absolute inset-0 bg-court-amber/15 mix-blend-overlay z-10 pointer-events-none" />
            <img src={THEO_IMG} alt="Theo running a training session on court" className="w-full h-[520px] object-cover object-center" data-testid="about-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-court-void/70 to-transparent z-10" />
            <div className="absolute bottom-5 left-5 z-20 bg-court-surface/90 backdrop-blur-md border border-court-amber/30 rounded-xl px-5 py-3">
              <div className="font-mono text-xs uppercase tracking-widest text-court-amber font-bold">Coach Theo</div>
              <div className="text-sm text-zinc-300">Founder, Theo Basketball Lab</div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="border-y border-white/10 bg-court-surface/40 court-grid-bg py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <SectionTag index="SEC.02" label="Player Development Pillars" />
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
              What gets built <span className="text-court-amber">in the Lab.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.07}>
                <div data-testid={`pillar-${i}`} className="card-lift bg-court-surface/90 border border-white/10 rounded-2xl p-6 h-full">
                  <div className="w-11 h-11 rounded-xl bg-court-amber/15 flex items-center justify-center mb-5">
                    <p.icon size={20} className="text-court-amber" />
                  </div>
                  <h3 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl">{p.title}</h3>
                  <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-24">
        <Reveal>
          <SectionTag index="SEC.03" label="For Parents" />
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
            Questions, <span className="text-court-amber">answered.</span>
          </h2>
        </Reveal>
        <div className="mt-10 space-y-3" data-testid="faq-list">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.04}>
              <div className={`border rounded-xl overflow-hidden transition-colors ${openFaq === i ? "border-court-amber/40 bg-court-surface/90" : "border-white/10 bg-court-surface/60"}`}>
                <button data-testid={`faq-question-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left">
                  <span className="font-display font-bold uppercase tracking-wide text-lg">{f.q}</span>
                  <ChevronDown size={18} className={`text-court-amber shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p data-testid={`faq-answer-${i}`} className="px-6 pb-5 text-sm text-zinc-400 leading-relaxed">{f.a}</p>}
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
